import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import regression from 'regression';
import moment from "moment";
import { MaterialModule } from 'src/app/app.materials';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-return',
  templateUrl: './return.component.html',
  standalone: true,
  imports: [MaterialModule, FormsModule, CommonModule]
})
export class ReturnComponent implements OnInit, OnChanges {

  @Input() index: any = {};
  @Input() benchmark: any = {};

  hasExposure = false;
  beta = null;
  alpha = null;

  hasOutperformance = false;
  outperformance = {
    month: null,
    quarter: null,
    year: null
  };

  returnList = [];

  constructor() { }

  ngOnInit(): void {
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.index) {
      this.index = changes.index.currentValue;
      this.updateLists();
    }
    if (changes.benchmark) {
      this.benchmark = changes.benchmark.currentValue;
      this.updateLists();
      if (this.benchmark) {
        this.calculateLinearRegression();
      }
    }
  }

  extractBacktestDateAndValue(list) {
    const result = [];
    list.forEach(data => {
      result[data.date] = {
        date: data.date,
        value: data.value
      };
    });
    return result;
  }

  calculateLinearRegression() {
    const indexDataAll: any[] = this.extractBacktestDateAndValue(this.index.backtestPerformance);
    const benchmarkDataAll = this.extractBacktestDateAndValue(this.benchmark.backtestPerformance);

    const indexData: any[] = [];
    const benchmarkData: any[] = [];

    const regressionData = [];

    let indexVal = 0;
    let benchmarkVal = 0;

    let index = 0;

    const monthlyGrouping: any = {
      index: [],
      benchmark: []
    };
    const quarterlyGrouping: any = {
      index: [],
      benchmark: []
    };
    const yearlyGrouping: any = {
      index: [],
      benchmark: []
    };

    Object.values(indexDataAll).map((data) => {
      if (benchmarkDataAll[data.date]) {
        indexData.push(data);
        benchmarkData.push(benchmarkDataAll[data.date]);
        if (indexData.length > 1) {
          // Gustav said:
          // Looking at the data that the input is not in "returns".
          // Could we make the input to the regression into returns i.e. ( P_t / P_(t-1)) -1 ?
          // So to compute the return the price at time t (P_t) should be divided with the price at time t-1 (P_(t-1)) and then substract 1 from it.
          // So for example (1.0043852 / 1 ) -1 is the return for the benchmark at 1996-01-03

          indexVal = (indexData[index].value / indexData[index - 1].value) - 1;   // return calc
          benchmarkVal = (benchmarkData[index].value / benchmarkData[index - 1].value) - 1;   // return calc

          let dateMoment = moment(data.date);

          let year = dateMoment.format('YYYY');
          let month = dateMoment.format('YYYY-M');
          let quarterNum = dateMoment.quarter();
          let quarter = `${year}-Q${quarterNum}`;

          // Index grouping for outperformance
          if (!monthlyGrouping.index[month]) {
            monthlyGrouping.index[month] = [];
          }
          monthlyGrouping.index[month].push(indexVal);

          if (!quarterlyGrouping.index[quarter]) {
            quarterlyGrouping.index[quarter] = [];
          }
          quarterlyGrouping.index[quarter].push(indexVal);

          if (!yearlyGrouping.index[year]) {
            yearlyGrouping.index[year] = [];
          }
          yearlyGrouping.index[year].push(indexVal);


          // Benchmark grouping for outperformance
          if (!monthlyGrouping.benchmark[month]) {
            monthlyGrouping.benchmark[month] = [];
          }
          monthlyGrouping.benchmark[month].push(benchmarkVal);

          if (!quarterlyGrouping.benchmark[quarter]) {
            quarterlyGrouping.benchmark[quarter] = [];
          }
          quarterlyGrouping.benchmark[quarter].push(benchmarkVal);

          if (!yearlyGrouping.benchmark[year]) {
            yearlyGrouping.benchmark[year] = [];
          }
          yearlyGrouping.benchmark[year].push(benchmarkVal);

          // Regression input list
          regressionData.push([benchmarkVal, indexVal]);
        }
        index++;
      }
    });

    this.calculateOutperformnace(monthlyGrouping, quarterlyGrouping, yearlyGrouping);

    const result = regression.linear(regressionData, { precision: 6 });
    this.beta = result.equation[0];

    // Niklas
    // Note also that the values should be rounded to 3 decimals when displayed.
    // The alpha value should also be multiplied by 100*252 to convert to percentages and to annualize (not the beta).
    this.alpha = result.equation[1] * 100 * 252;
    this.hasExposure = true;
  }

  standardDeviation(values){
    const avg = this.average(values);

    const squareDiffs = values.map((value) => {
      const diff = value - avg;
      return diff * diff;
    });

    const avgSquareDiff = this.average(squareDiffs);

    const stdDev = Math.sqrt(avgSquareDiff);
    return stdDev;
  }

  average(data){
    const sum = data.reduce((s, value) => {
      return s + value;
    }, 0);
    return sum / data.length;
  }



  calculatePerformanceMeasure(list) {
    const mean = this.average(list);

    if (mean < 0) {
      return mean;
    } else {
      return mean / this.standardDeviation(list);
    }
  }

  makePerformanceMeasureList(dateFrame) {
    const result: any[] = [];

    Object.entries(dateFrame).map(([key, val]) => {
      result[key] = this.calculatePerformanceMeasure(val);
    });

    return result;
  }

  calculateOutperformanceRatio(list) {
    let outPeform = 0;
    let count = 0;
    Object.entries(list.index).map(([key, index]) => {
      let benchmark = list.benchmark[key];
      if (index > benchmark) {
        outPeform++;
      }
      count++;
    });
    return (outPeform / count) * 100; // Ratio percentage
  }

  calculateOutperformnace(months, quarters, years) {
    console.log("### Outperformance Return Grouping");
    console.log("Monthly grouping:", months);
    console.log("Quarterly grouping:", quarters);
    console.log("Yearly grouping:", years);
    console.log(" - - - ")

    // Performance measures
    const performanceMeasureMonths = {
      index: this.makePerformanceMeasureList(months.index),
      benchmark: this.makePerformanceMeasureList(months.benchmark),
    };

    const performanceMeasureQuarters = {
      index: this.makePerformanceMeasureList(quarters.index),
      benchmark: this.makePerformanceMeasureList(quarters.benchmark),
    };

    const performanceMeasureYears = {
      index: this.makePerformanceMeasureList(years.index),
      benchmark: this.makePerformanceMeasureList(years.benchmark),
    };

    console.log("### Performance Measure results");
    console.log("Monthly outperformance", performanceMeasureMonths);
    console.log("Quarterly outperformance", performanceMeasureQuarters);
    console.log("Yearly outperformance", performanceMeasureYears);
    console.log(" - - - ")

    this.outperformance.month = this.calculateOutperformanceRatio(performanceMeasureMonths);
    this.outperformance.quarter = this.calculateOutperformanceRatio(performanceMeasureQuarters);
    this.outperformance.year = this.calculateOutperformanceRatio(performanceMeasureYears);

    console.log("Outperformance ratio");
    console.log(this.outperformance);

    this.hasOutperformance = true;
  }

  updateLists() {
    this.returnList = [];

    this.checkModel(this.index);
    this.checkModel(this.benchmark);
  }

  checkModel(model) {
    if (model && model.returnn && model.returnn.length) {
      model.returnn[0].index = model.name;
      this.returnList.push(model.returnn[0]);
    }
  }

}
